import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Chip,
  DesignV2Scope,
  EmptyState,
  HorizontalRail,
  Skeleton,
} from "../design-v2";
import {
  CardioCard,
  ExerciseCard,
  GearIcon,
  MetricTile,
  MuscleDistribution,
  StackHeader,
  SupersetGroup,
  UnsupportedBlockCard,
} from "../components/product";
import { useManualProgram } from "../context/ManualProgramContext";
import { buildOrigin, resolveBackTarget } from "../features/weeklyPlans/navigation";
import {
  getManualBuilderPath,
  getWeeklyPlanDetailsPath,
  getWeeklyPlansPath,
} from "../features/weeklyPlans/routes";
import { mapWeeklyPlanDetailsToUi } from "../features/weeklyPlans/mappers";
import {
  getWeeklyPlanById,
  openOrCreateWeeklyPlanEditDraft,
} from "../services/api";
import { PlanMetadata } from "./programLibraryShared";
import { toViewerBlock } from "./weeklyPlanViewerAdapter";
import { toMuscleVolumeItems } from "../features/muscleVolume/plannedMuscleVolume";
import "./programDetails.css";

const TUT_HELP =
  "Time Under Tension estimates how long your muscles are working during the planned sets. It adds context to reps and tempo, but is not a standalone measure of muscle growth.";

function BoltIcon() {
  return (
    <svg className="lz-plan-viewer__bolt-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.5 2.5 5.5 13h5.25L9.5 21.5l9-11.5h-5.75l.75-7.5Z" />
    </svg>
  );
}

function BlockRenderer({ block, position }) {
  if (block.type === "single") {
    return <ExerciseCard block={block} position={position} />;
  }
  if (block.type === "superset") {
    return <SupersetGroup block={block} position={position} />;
  }
  if (block.type === "cardio") {
    return <CardioCard block={block} position={position} />;
  }
  return <UnsupportedBlockCard block={block} position={position} />;
}

function PlanViewerLoading() {
  return (
    <DesignV2Scope
      className="lz-plan-viewer lz-plan-viewer--loading"
      aria-busy="true"
      aria-label="Loading program details"
    >
      <header className="lz-plan-viewer__loading-header">
        <Skeleton shape="circle" width={44} aria-label="Loading back control" />
        <Skeleton shape="text" width="55%" aria-label="Loading program title" />
        <Skeleton shape="circle" width={44} aria-label="Loading program action" />
      </header>
      <main className="lz-plan-viewer__content">
        <div className="lz-plan-viewer__loading-hero">
          <Skeleton shape="text" width="35%" aria-label="Loading program eyebrow" />
          <Skeleton shape="text" width="72%" aria-label="Loading program name" />
          <Skeleton shape="block" height={56} aria-label="Loading training action" />
        </div>
        <div className="lz-plan-viewer__loading-metrics">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton shape="block" height={72} aria-label={`Loading metric ${index + 1}`} key={index} />
          ))}
        </div>
        <Skeleton shape="block" height={120} aria-label="Loading muscle distribution" />
        <Skeleton shape="block" height={44} aria-label="Loading workout navigation" />
        <Skeleton shape="block" height={240} aria-label="Loading exercise card" />
      </main>
    </DesignV2Scope>
  );
}

export default function ProgramDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { programId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { draftMetadata, hydrateProgramDraft, beginHydrationTarget } = useManualProgram();
  const [program, setProgram] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningDraft, setIsOpeningDraft] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadProgram() {
      setIsLoading(true);

      try {
        const response = await getWeeklyPlanById(programId);
        if (isMounted) {
          setProgram(mapWeeklyPlanDetailsToUi(response));
        }
      } catch (error) {
        if (isMounted) {
          setProgram(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadProgram();

    return () => {
      isMounted = false;
    };
  }, [programId]);

  const activeWorkoutId = searchParams.get("workoutId");
  const activeWorkout =
    program?.workouts.find((workout) => workout.id === activeWorkoutId) ||
    program?.workouts[0] ||
    null;

  useEffect(() => {
    if (!program?.workouts?.length || !activeWorkout) {
      return;
    }

    if (searchParams.get("workoutId") === activeWorkout.id) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("workoutId", activeWorkout.id);
    setSearchParams(nextParams, { replace: true });
  }, [activeWorkout, program, searchParams, setSearchParams]);

  const summary = program?.summary || {};

  // Canonical weighted volume computed by the backend with the Insights rules.
  const muscleVolumeItems = useMemo(
    () => toMuscleVolumeItems(summary.muscleVolume),
    [summary.muscleVolume]
  );

  const viewerBlocks = useMemo(
    () => (activeWorkout?.blocks || []).map((block) => toViewerBlock(block)).filter(Boolean),
    [activeWorkout]
  );

  const handleOpenDraft = async () => {
    if (!program) {
      return;
    }

    if (
      draftMetadata.loadedFromBackend &&
      draftMetadata.weeklyPlanParentId === program.weeklyPlanParentId &&
      draftMetadata.status === "draft"
    ) {
      // Already loaded (e.g. re-clicking Edit on the plan currently open in
      // the builder) -- reuse it instead of an unconditional re-fetch that
      // could force-hydrate over in-flight/dirty local edits.
      navigate(getManualBuilderPath(), {
        state: {
          from: buildOrigin(location),
          returnTo: resolveBackTarget(location, getWeeklyPlansPath()),
        },
      });
      return;
    }

    setIsOpeningDraft(true);

    // Declared synchronously, before the fetch dispatches (plan §D): if the
    // user leaves this page for a different weekly plan before this
    // resolves, that navigation declares its own target, and this response
    // is dropped by hydrateProgramDraft instead of silently applying over
    // whatever the user is now looking at.
    try {
      await beginHydrationTarget({
        weeklyPlanParentId: program.weeklyPlanParentId,
        weeklyPlanVersionId: null,
      });
      const response = await openOrCreateWeeklyPlanEditDraft(program.weeklyPlanParentId);
      hydrateProgramDraft(response, {
        originRoute: buildOrigin(location),
      });
      navigate(getManualBuilderPath(), {
        state: {
          from: buildOrigin(location),
          returnTo: resolveBackTarget(location, getWeeklyPlansPath()),
        },
      });
    } catch (error) {
      // Keep the current details view if opening the draft fails.
    } finally {
      setIsOpeningDraft(false);
    }
  };

  if (isLoading) {
    return <PlanViewerLoading />;
  }

  if (!program) {
    return (
      <DesignV2Scope className="lz-plan-viewer">
        <StackHeader
          title="Program Details"
          backLabel="Back"
          onBack={() => navigate(resolveBackTarget(location, getWeeklyPlansPath()))}
        />
        <main className="lz-plan-viewer__content">
          <EmptyState
            title="Unable to load program"
            body="This weekly plan could not be loaded."
            action={
              <Button onClick={() => navigate(resolveBackTarget(location, getWeeklyPlansPath()))}>
                Back to programs
              </Button>
            }
          />
        </main>
      </DesignV2Scope>
    );
  }

  return (
    <DesignV2Scope className="lz-plan-viewer" data-testid="program-details-root">
      <StackHeader
        title={program.name}
        backLabel="Back"
        onBack={() => navigate(resolveBackTarget(location, getWeeklyPlansPath()))}
        trailing={
          <Button
            variant="secondary"
            size="icon"
            className="lz-plan-viewer__edit-action"
            aria-label="Edit program settings"
            onClick={handleOpenDraft}
            isLoading={isOpeningDraft}
            loadingLabel="Opening draft..."
          >
            <GearIcon />
          </Button>
        }
      />

      <main className="lz-plan-viewer__content">
        <section className="lz-plan-viewer__hero">
          <p className="lz-plan-viewer__eyebrow">Program name</p>
          <h2 className="lz-plan-viewer__title">{program.name}</h2>
          <PlanMetadata source={program.source} status={program.status} />
          <Button className="lz-plan-viewer__start" size="lg" disabled>
            Start training
            <BoltIcon />
          </Button>
        </section>

        <div
          className="lz-product-metrics lz-product-metrics--section lz-product-metrics--aligned lz-plan-viewer__metrics"
          role="region"
          aria-label="Weekly plan metrics"
        >
          <MetricTile
            label="Exercises"
            value={summary.totalExercises ?? 0}
            barValue={summary.totalExercises ?? 0}
            barMax={32}
          />
          <MetricTile
            label="Sets"
            value={summary.totalWeeklySets ?? 0}
            barValue={summary.totalWeeklySets ?? 0}
            barMax={80}
          />
          <MetricTile
            label={summary.durationEstimate ? "Est. time" : "Avg. time"}
            value={`${summary.averageWorkoutDurationMinutes ?? 0}m`}
            barValue={summary.averageWorkoutDurationMinutes ?? 0}
            barMax={120}
          />
          <MetricTile
            label="Avg. TUT"
            value={
              summary.averageWorkoutTUTMinutes == null
                ? "N/A"
                : `${summary.averageWorkoutTUTMinutes}m`
            }
            barValue={summary.averageWorkoutTUTMinutes ?? 0}
            barMax={60}
            helpTitle="Time Under Tension"
            helpBody={TUT_HELP}
          />
        </div>

        <MuscleDistribution
          items={muscleVolumeItems}
          title="Weekly Muscle Distribution"
          weighted
          unclassifiedSets={summary.muscleVolume?.unclassifiedSets}
          status={summary.muscleVolume ? "ready" : "error"}
        />

        {program.workouts.length > 0 ? (
          <HorizontalRail
            className="lz-plan-viewer__workout-rail"
            aria-label="Workout selection"
            activeItem={activeWorkout?.id}
            snap="start"
          >
            {program.workouts.map((workout) => {
              const isActive = workout.id === activeWorkout?.id;

              return (
                <Chip
                  key={workout.id}
                  data-rail-item={workout.id}
                  selected={isActive}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() =>
                    navigate(
                      getWeeklyPlanDetailsPath(program.weeklyPlanParentId, workout.id),
                      {
                        replace: true,
                        state: location.state,
                      }
                    )
                  }
                >
                  Workout {workout.name.split(":")[0].replace("Workout ", "")}
                </Chip>
              );
            })}
          </HorizontalRail>
        ) : null}

        {activeWorkout ? (
          <>
            <section className="lz-plan-viewer__workout-heading">
              <h3 className="lz-plan-viewer__workout-title">{activeWorkout.name}</h3>
              <dl className="lz-plan-viewer__workout-stats">
                <div>
                  <dt>Exercises</dt>
                  <dd>{activeWorkout.metrics.exerciseCount}</dd>
                </div>
                <div>
                  <dt>Sets</dt>
                  <dd>{activeWorkout.metrics.setCount}</dd>
                </div>
                <div>
                  <dt>{activeWorkout.metrics.estimatedDurationRange ? "Est. duration" : "Duration"}</dt>
                  <dd>
                    {activeWorkout.metrics.estimatedDurationRange
                      ? `${activeWorkout.metrics.estimatedDurationMinutes} min (${activeWorkout.metrics.estimatedDurationRange.low}-${activeWorkout.metrics.estimatedDurationRange.high})`
                      : `${activeWorkout.metrics.estimatedDurationMinutes} min`}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="lz-plan-viewer__program" aria-labelledby="plan-viewer-program-title">
              <h2 className="lz-product-section-title" id="plan-viewer-program-title">
                Program
              </h2>
              {viewerBlocks.length > 0 ? (
                <div className="lz-plan-viewer__blocks">
                  {viewerBlocks.map((block, index) => (
                    <BlockRenderer
                      block={block}
                      position={index + 1}
                      key={block.id || `${block.type}-${index}`}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No exercises in this workout"
                  body="This workout does not contain any exercise blocks yet."
                />
              )}
            </section>
          </>
        ) : (
          <EmptyState
            title="No workout details yet"
            body="No workout details are available for this weekly plan yet."
          />
        )}
      </main>
    </DesignV2Scope>
  );
}
